import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
} from "@angular/core";
import { Capacitor, PluginListenerHandle } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { Platform } from "@ionic/angular";
import {
  isFocusableFormField,
  scheduleScrollFieldAboveKeyboard,
  scheduleScrollFocusedFieldAboveKeyboard,
} from "src/app/helpers/keyboard-input-scroll";
import { KeyboardService } from "src/app/_services/keyboard.service";

@Directive({
  selector: "ion-content",
})
export class IosKeyboardScrollDirective implements AfterViewInit, OnDestroy {
  private keyboardSubscription: { unsubscribe?: () => void } | null = null;
  private keyboardListeners: PluginListenerHandle[] = [];
  private focusListeners: Array<() => void> = [];
  private mutationObserver: MutationObserver | null = null;
  private setupTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private platform: Platform,
    private keyboardService: KeyboardService
  ) {}

  ngAfterViewInit(): void {
    if (!this.platform.is("ios")) {
      return;
    }

    this.setupKeyboardHandling();
    this.setupTimeout = setTimeout(() => this.setupInputFocusHandlers(), 300);
  }

  ngOnDestroy(): void {
    this.cleanupKeyboardHandling();
  }

  private setupKeyboardHandling(): void {
    this.keyboardSubscription = this.keyboardService
      .getKeyboardState()
      .subscribe((isVisible) => {
        if (!isVisible) {
          return;
        }
        const keyboardHeight = this.keyboardService.getCurrentKeyboardHeight();
        scheduleScrollFocusedFieldAboveKeyboard(keyboardHeight);
      });

    if (Capacitor.isNativePlatform()) {
      void Keyboard.addListener("keyboardWillShow", (info) => {
        scheduleScrollFocusedFieldAboveKeyboard(info.keyboardHeight);
      }).then((handle) => this.keyboardListeners.push(handle));

      void Keyboard.addListener("keyboardDidShow", (info) => {
        scheduleScrollFocusedFieldAboveKeyboard(info.keyboardHeight, [100, 250]);
      }).then((handle) => this.keyboardListeners.push(handle));
    }
  }

  private setupInputFocusHandlers(): void {
    this.cleanupFocusListeners();

    const contentElement = this.elementRef.nativeElement;
    if (!contentElement) {
      return;
    }

    const handleFocus = (target: HTMLElement) => {
      if (!contentElement.contains(target) || !isFocusableFormField(target)) {
        return;
      }

      const keyboardHeight = Math.max(
        this.keyboardService.getCurrentKeyboardHeight(),
        280
      );
      scheduleScrollFieldAboveKeyboard(target, keyboardHeight);
    };

    const globalFocusHandler = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      if (target) {
        handleFocus(target);
      }
    };

    document.addEventListener("focusin", globalFocusHandler, true);
    this.focusListeners.push(() => {
      document.removeEventListener("focusin", globalFocusHandler, true);
    });

    const bindInputFocus = () => {
      const inputs = contentElement.querySelectorAll(
        "input, textarea, ion-input, ion-textarea"
      );
      inputs.forEach((input) => {
        const focusHandler = () => handleFocus(input as HTMLElement);
        input.addEventListener("focus", focusHandler);
        this.focusListeners.push(() => {
          input.removeEventListener("focus", focusHandler);
        });
      });
    };

    bindInputFocus();

    if (!this.mutationObserver) {
      this.mutationObserver = new MutationObserver(() => {
        if (this.setupTimeout) {
          clearTimeout(this.setupTimeout);
        }
        this.setupTimeout = setTimeout(() => bindInputFocus(), 200);
      });

      this.mutationObserver.observe(contentElement, {
        childList: true,
        subtree: true,
      });
    }
  }

  private cleanupKeyboardHandling(): void {
    this.keyboardSubscription?.unsubscribe?.();
    this.keyboardSubscription = null;
    this.keyboardListeners.forEach((listener) => void listener.remove());
    this.keyboardListeners = [];
    this.cleanupFocusListeners();
  }

  private cleanupFocusListeners(disconnectObserver = true): void {
    this.focusListeners.forEach((cleanup) => cleanup());
    this.focusListeners = [];

    if (disconnectObserver && this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    if (this.setupTimeout) {
      clearTimeout(this.setupTimeout);
      this.setupTimeout = null;
    }
  }
}
